import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import cuid from 'cuid';
import PropTypes from 'prop-types';
import Loader from '@/components/common/Loader';
import LoaderSizes from '@/constants/ui/LoaderSizes';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { useInfiniteFeed } from '@/modules/Feed';
import useFeedType from '../lib/useFeedType';
import FeedItem from './FeedItem';

function Feed({ className, id: feedId, scrollableTarget }) {
  const feedType = useFeedType();
  const { campaignId, constituentId, id, historyId } = useParams();

  // Determine scrollable target for InfiniteScroll component.
  const [containerId] = React.useState(feedId || `feed-container-${cuid()}`);

  // Fetch feed data.
  const feedParams = { campaignId, constituentId };
  const { data: feedData, size, setSize } = useInfiniteFeed(feedParams);
  const items = React.useMemo(
    () =>
      feedData
        ? feedData.reduce(
            (arr, page) =>
              page?.feedItems ? [...arr, ...page.feedItems] : arr,
            [],
          )
        : null,
    [feedData],
  );
  const total = React.useMemo(
    () => (feedData || []).find(Boolean)?.totalHits || null,
    [feedData],
  );
  const onNextPage = React.useCallback(
    () => setSize(size + 1),
    [setSize, size],
  );

  // Determine active feed entity.
  const [activeItem, setActiveItem] = React.useState(null);
  const activeItemId = id || historyId; // TODO(jon): This can be simplified if we make the route params the same name.
  React.useEffect(() => {
    // Items not loaded or activeItem is correctly set. Do nothing.
    if (!items || (activeItemId && activeItem?.feedItemId === activeItemId))
      return;

    // No active item selected. Unset activeItem.
    if (!activeItemId) {
      setActiveItem(null);
      return;
    }

    // Active item selected, find it.
    const newActiveItem = items.find(
      (item) => item.feedItemId === activeItemId,
    );
    if (newActiveItem) {
      setActiveItem(newActiveItem);
      return;
    }

    // No matching item found. Grab the next page of results. What's happening
    // here is likely that someone scrolled down a few pages, opened up a feed
    // item, and then refreshed the page. The feed re-initializes with only the
    // first page, but we don't have the data for the desired active item, so
    // we need to recreate the user's path through pagination.
    onNextPage();
  }, [activeItemId, items]);

  // Scroll the active item into view.
  React.useEffect(() => {
    if (!activeItem) return;
    document
      .getElementById(`${containerId}-${activeItemId}`)
      .scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeItem]);

  if (!items)
    return <Loader isCentered className="h-full w-full items-center" />;

  return (
    <div
      key={scrollableTarget} // Key changes force the infinite scroll to requery its scrollableTarget, which is useful when breakpoints change
      className={classNames(className, 'relative overflow-y-auto bg-grey-100')}
      id={containerId}
    >
      {items.length ? (
        <InfiniteScroll
          dataLength={items.length}
          scrollableTarget={scrollableTarget}
          hasMore={items.length < total}
          className="grid grid-cols-1 gap-4 p-4"
          next={onNextPage}
          loader={
            <Loader isCentered className="my-8 w-full" size={LoaderSizes.SM} />
          }
        >
          {items.map((item) => (
            <FeedItem
              feedType={feedType}
              feedItem={item}
              id={`${containerId}-${item.feedItemId}`}
              key={item.feedItemId}
            />
          ))}
        </InfiniteScroll>
      ) : (
        <p className="my-20 text-center">{translate('EMPTY_FEED')}</p>
      )}
    </div>
  );
}

Feed.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  scrollableTarget: PropTypes.string,
};

Feed.defaultProps = {
  className: '',
  id: null,
  scrollableTarget: 'root',
};

export default Feed;
